import React from 'react';

/**
 * The time zone this viewer picked on a board, kept in their browser. On a
 * first visit there is none, and the page sends the browser's own zone for
 * the server to match ("America/Chicago" reads as CT). Storage can be blocked
 * or wiped, so every read and write is allowed to fail.
 *
 * Shared by every board with a time zone menu — the Daily Call Plan's
 * suggested times, Campaign Performance's hours of the day — so a viewer's
 * pick works the same on each.
 */
const zoneKey = (dashboardId: string) => `klaris.lookup.zone.${dashboardId}`;

function storedZone(dashboardId: string | undefined): string | null {
  if (!dashboardId) return null;
  try {
    return window.localStorage.getItem(zoneKey(dashboardId));
  } catch {
    return null;
  }
}

function browserZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/** The zone to send for this board, and how to pick another (remembered). */
export function useBoardZone(dashboardId: string | undefined): [string | undefined, (next: string) => void] {
  const [zone, setZone] = React.useState<string | undefined>(() => storedZone(dashboardId) ?? browserZone());
  // Another board in the same page (the route keeps it mounted): its own pick.
  const [seenFor, setSeenFor] = React.useState(dashboardId);
  if (seenFor !== dashboardId) {
    setSeenFor(dashboardId);
    setZone(storedZone(dashboardId) ?? browserZone());
  }
  const pickZone = React.useCallback((next: string) => {
    setZone(next);
    try {
      if (dashboardId) window.localStorage.setItem(zoneKey(dashboardId), next);
    } catch {
      // Not remembered this time; the pick still applies.
    }
  }, [dashboardId]);
  return [zone, pickZone];
}
